// ── users-write: honest write-error + role-grant helpers ───────────────────
// Wave 1 (Users). Shared by the user write flows (create / edit / deactivate).
// A write failure is surfaced INLINE — never swallowed, never faked — and a gate
// / permission-denied / unavailable write reads as its honest message, NEVER an
// implied success. The single CO_* -> posture map is reused so the modals speak
// the same vocabulary as the list reads.

import { ApiClientError } from "../../services/api-client";
import type { CoachingRecordInput } from "../../services/coaching-dtos";
import { canGrantRole, type CoachingRole } from "../../domain/roles";
import {
  honestGateFromError,
  postureForCoachingError,
  referenceFromError
} from "../../services/coaching-error-posture";
import type { UserFormValues } from "./user-form-modal";

export interface WriteErrorView {
  tone: "error" | "warning";
  message: string;
}


/** Explicit write-body projection. The PATCH contract uses `active`: true
 * reactivates an inactive user and false suspends a user. Inactive is a
 * separate deactivation workflow; preserving it here avoids silently
 * converting a deactivated readback into suspension. */
export function userWriteBody(values: UserFormValues): CoachingRecordInput {
  const body: CoachingRecordInput = {
    name: values.name,
    email: values.email,
    role: values.role
  };
  if (values.role === "company_admin") {
    body.companyIds = [Number(values.companyId)];
  } else if (values.role === "cohort_lead") {
    body.cohortIds = values.cohortIds.map(Number);
  }
  if (values.status === "active") return { ...body, active: true };
  if (values.status === "suspended") return { ...body, active: false };
  return body;
}

/** Map a user write error to an honest, non-leaking top-level message. */
export function describeUserWriteError(error: unknown): WriteErrorView | null {
  if (error == null) return null;
  if (!(error instanceof ApiClientError)) {
    return { tone: "error", message: "Couldn't save the user. Please try again." };
  }
  const posture = postureForCoachingError(error);
  const reference = referenceFromError(error);
  const ref = reference ? ` (reference: ${reference})` : "";

  if (error.code === "CO_USER_ALREADY_EXISTS" || error.status === 409) {
    return {
      tone: "error",
      message: "A user with this email already exists in this Coaching organization."
    };
  }
  if (posture === "permission-denied") {
    return {
      tone: "error",
      message: "Your role can't make this change. Granting the admin role requires a super admin."
    };
  }
  if (posture === "gate-closed") {
    const gate = honestGateFromError(error);
    return { tone: "warning", message: `${gate?.reason ?? "This action is closed right now."} No change was made.` };
  }
  if (posture === "unavailable") {
    return { tone: "error", message: "Coaching is temporarily unavailable. Try again in a moment." };
  }
  return { tone: "error", message: `${error.message}${ref}` };
}

// ── The roles an admin-tier actor can ASSIGN to a user (create/edit) ───────
// Super admins may grant every role. Normal admins may grant operational roles
// only; the server re-checks this policy and returns 403 on forged requests.
export const ASSIGNABLE_ROLES: CoachingRole[] = [
  "coach",
  "cohort_lead",
  "company_admin",
  "participant",
  "admin",
  "super_admin"
];

/** The subset of roles the acting role may actually grant (UI affordance gate). */
export function grantableRoles(actor: CoachingRole): CoachingRole[] {
  return ASSIGNABLE_ROLES.filter((target) => canGrantRole(actor, target));
}

export const USER_STATUSES = ["active", "suspended"] as const;
export const USER_FILTER_STATUSES = ["active", "suspended", "inactive"] as const;
export type UserStatus = (typeof USER_FILTER_STATUSES)[number];
